/**
 * This file will contain validation helper functions
 * to be used with react-hook-form validations
 */

/**
 * Checks if any value exist after removing the white spaces
 *
 * @param {string} value - Input value
 * @return {boolean} a boolean indicating either value without spaces exist or not.
 */
const isValueExist = (value) => !!value?.trim()?.length;

/**
 * Validates the email
 *
 * @param {string} email - Input value
 * @return {boolean} a boolean indicating either email is correct or not.
 */
const isValidEmail = (email) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/g.test(email);

const isValidSpecialRequirementString = (value) => isValueExist(value);

const isValidSpecialRequirementInteger = (value) => /^-?\d+$/.test(String(value ?? "").trim());

const isValidSpecialRequirementDouble = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  return /^-?\d+(\.\d+)?$/.test(trimmed) && Number.isFinite(Number(trimmed));
};

const isValidSpecialRequirementAdditionalInformation = (value, dataType, additionalInformationRequired = true) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return additionalInformationRequired === false;

  switch (dataType) {
    case "integer":
      return isValidSpecialRequirementInteger(value);
    case "double":
      return isValidSpecialRequirementDouble(value);
    case "email":
      return isValidEmail(String(value ?? "").trim());
    case "string":
    default:
      return isValidSpecialRequirementString(value);
  }
};

const getSpecialRequirementValidationMessage = (dataType) => {
  switch (dataType) {
    case "integer":
      return "Must be a valid integer.";
    case "double":
      return "Must be a valid number using digits and a decimal point only (e.g. 3.14 or -3.14).";
    case "email":
      return "Must be a valid email address.";
    case "string":
    default:
      return "This field is required.";
  }
};

const isPartialSpecialRequirementDouble = (value) => /^-?\d*\.?\d*$/.test(String(value ?? ""));

const isPartialSpecialRequirementInteger = (value) => /^-?\d*$/.test(String(value ?? ""));

const isPartialSpecialRequirementEmail = (value) => /^[\w.+-@]*$/.test(String(value ?? ""));

const coerceSpecialRequirementInteger = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";

  const digits = trimmed.match(/\d+/g);
  if (!digits?.length) return "";

  const result = digits.join("");
  return trimmed.startsWith("-") ? `-${result}` : result;
};

const coerceSpecialRequirementDouble = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";

  const decimalMatch = trimmed.match(/-?\d+\.\d+/);
  if (decimalMatch) return decimalMatch[0];

  return coerceSpecialRequirementInteger(trimmed);
};

const coerceSpecialRequirementEmail = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";

  const match = trimmed.match(/[\w.+-]+@[\w.-]+\.\w{2,}/);
  return match ? match[0] : "";
};

const setSpecialRequirementAdditionalInformationValue = (value, dataType) => {
  const stringValue = String(value ?? "");
  if (!stringValue) return stringValue;

  switch (dataType) {
    case "integer":
      if (isPartialSpecialRequirementInteger(stringValue)) return stringValue;
      return coerceSpecialRequirementInteger(stringValue);
    case "double":
      if (isPartialSpecialRequirementDouble(stringValue)) return stringValue;
      return coerceSpecialRequirementDouble(stringValue);
    case "email":
      if (isPartialSpecialRequirementEmail(stringValue)) return stringValue;
      return coerceSpecialRequirementEmail(stringValue);
    default:
      return value;
  }
};

const getDoubleDecimalPlaces = (value) => {
  const unsigned = String(value ?? "")
    .trim()
    .replace(/^-/, "");
  if (!unsigned.includes(".")) return 2;
  return unsigned.split(".")[1]?.length || 2;
};

const stepSpecialRequirementNumeric = (value, direction, dataType) => {
  if (dataType === "integer") {
    const parsed = parseInt(String(value ?? "").trim(), 10);
    const current = Number.isNaN(parsed) ? 0 : parsed;
    return String(current + direction);
  }

  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    const next = direction > 0 ? 0.01 : -0.01;
    return next.toFixed(2);
  }

  const decimalPlaces = getDoubleDecimalPlaces(trimmed);
  const step = 10 ** -decimalPlaces;
  const num = parseFloat(trimmed);

  if (Number.isNaN(num)) return trimmed;

  return (num + direction * step).toFixed(decimalPlaces);
};

export {
  isValueExist,
  isValidEmail,
  isValidSpecialRequirementString,
  isValidSpecialRequirementInteger,
  isValidSpecialRequirementDouble,
  isValidSpecialRequirementAdditionalInformation,
  getSpecialRequirementValidationMessage,
  isPartialSpecialRequirementDouble,
  isPartialSpecialRequirementInteger,
  isPartialSpecialRequirementEmail,
  coerceSpecialRequirementInteger,
  coerceSpecialRequirementDouble,
  coerceSpecialRequirementEmail,
  setSpecialRequirementAdditionalInformationValue,
  stepSpecialRequirementNumeric,
};
